import { Injectable } from '@angular/core'
import { isNotNil } from '@engineering11/utility'
import { Store } from '@ngrx/store'
import { Observable, ReplaySubject, Subject, asapScheduler, filter } from 'rxjs'
import { debounceTime, distinctUntilChanged, map, observeOn, switchMap } from 'rxjs/operators'
import { DEFAULT_NAV_FAVORITES, INavFavoritesDocument } from '../models/nav-favorites.model'
import { NavFavoritesRepository } from '../repositories/nav-favorites.repository'
import { selectors } from '../store/selectors'

export const NAV_FAVORITES_MAX = 50

/** Rapid toggles inside this window collapse into a single write */
const NAV_FAVORITES_DEBOUNCE_MS = 500

const sameContent = (a: string[], b: string[]): boolean => a.length === b.length && a.every((id, index) => id === b[index])

/**
 * Absent doc (never customized) → defaults; an existing doc is authoritative — `favorites: []` means the
 * user unstarred everything, so the defaults must NOT reappear. Defaults are copied so no consumer of
 * favorites$ can mutate the shared constant.
 */
const docToFavorites = (doc: INavFavoritesDocument | undefined): string[] => (doc ? doc.favorites ?? [] : [...DEFAULT_NAV_FAVORITES])

/** CNCT-6764: left-nav Favorites — realtime reads of the user's document, optimistic local toggles, debounced upsert writes */
@Injectable({ providedIn: 'root' })
export class NavFavoritesService {
  private userId$: Observable<string> = this.store.select(selectors.getCurrentUserEmployer).pipe(
    filter(isNotNil),
    map(user => user.id),
    distinctUntilChanged()
  )

  private current: string[] = []

  private userId?: string

  /**
   * Toggles before the first document emission are ignored: they would compute against the
   * default `current = []` and the debounced upsert could wipe the user's server-side favorites.
   */
  private userLoaded = false

  private favoritesSubject = new ReplaySubject<string[]>(1)

  private persist$ = new Subject<{ userId: string; favorites: string[] }>()

  private dispatchPending = false

  // observeOn(asapScheduler) is deliberate: the ReplaySubject can otherwise emit synchronously mid change-detection (NG0100)
  readonly favorites$: Observable<string[]> = this.favoritesSubject.pipe(observeOn(asapScheduler), distinctUntilChanged(sameContent))

  constructor(private store: Store, private repository: NavFavoritesRepository) {
    this.persist$.pipe(debounceTime(NAV_FAVORITES_DEBOUNCE_MS)).subscribe(({ userId, favorites }) => {
      this.dispatchPending = false
      // Never write one user's list onto another's doc (user switched inside the debounce window)
      if (userId !== this.userId) return
      this.repository.upsert({ id: userId, favorites })
    })
    this.userId$
      .pipe(switchMap(userId => this.repository.getValueChanges(userId).pipe(map(doc => ({ userId, favorites: docToFavorites(doc) })))))
      .subscribe(({ userId, favorites }) => {
        // A different user's document invalidates any pending toggle from the previous user
        if (userId !== this.userId) this.dispatchPending = false
        this.userId = userId
        this.userLoaded = true
        if (!this.dispatchPending) this.setCurrent(favorites)
      })
  }

  isFavorite$(favoriteId: string): Observable<boolean> {
    return this.favorites$.pipe(
      map(favorites => favorites.includes(favoriteId)),
      distinctUntilChanged()
    )
  }

  toggleFavorite(favoriteId: string): void {
    if (!this.userLoaded) return
    const next = this.current.includes(favoriteId) ? this.current.filter(id => id !== favoriteId) : [...this.current, favoriteId]
    if (next.length > NAV_FAVORITES_MAX) return
    this.dispatchPending = true
    this.setCurrent(next)
    // userLoaded guarantees userId is set here
    this.persist$.next({ userId: this.userId!, favorites: next })
  }

  private setCurrent(favorites: string[]): void {
    this.current = favorites
    this.favoritesSubject.next(favorites)
  }
}
